/**
 * Sentry `beforeSend` hook that strips PII and secrets from events.
 * Attach to every Sentry init call across PetaCMS workers and frontends.
 *
 * Phase 2: Sentry introduced.
 * Phase 9: security audit verifies this hook is active in production.
 */

import { MASKED_VALUE, maskHeaders, maskObject, maskUrl } from './masking.js';

/**
 * Minimal subset of Sentry's event shape we care about.
 * We avoid a direct dep on @sentry/core; the real callback will accept
 * this via structural typing.
 */
export interface SanitizableEvent {
  request?: {
    url?: string;
    headers?: Record<string, string>;
    cookies?: Record<string, string>;
    data?: unknown;
    query_string?: string;
  };
  user?: {
    id?: string;
    email?: string;
    ip_address?: string;
    username?: string;
  };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  tags?: Record<string, string>;
  breadcrumbs?: Array<{
    category?: string;
    message?: string;
    data?: unknown;
  }>;
}

export interface SanitizeOptions {
  /** Replace email with a hash instead of removing entirely. */
  hashEmail?: boolean;
  /** Strip IP address completely. GDPR safe default: true. */
  stripIp?: boolean;
  /** Keep the user.id but drop email / username. */
  keepUserId?: boolean;
}

const DEFAULT_OPTIONS: Required<SanitizeOptions> = {
  hashEmail: false,
  stripIp: true,
  keepUserId: true,
};

/**
 * Returns a sanitized event.
 * Pass this function (or its curried form) as `beforeSend` to Sentry.init.
 *
 * ```ts
 * Sentry.init({
 *   dsn: env.SENTRY_DSN,
 *   beforeSend: (event) => sanitizeSentryEvent(event),
 * });
 * ```
 */
export const sanitizeSentryEvent = <T extends SanitizableEvent>(
  event: T,
  options: SanitizeOptions = {},
): T => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const sanitized: T = { ...event };

  // --- request ---
  if (sanitized.request) {
    const req = { ...sanitized.request };
    if (req.url) req.url = maskUrl(req.url);
    if (req.headers) req.headers = maskHeaders(req.headers);
    if (req.cookies) req.cookies = { _scrubbed: MASKED_VALUE };
    if (req.data !== undefined) req.data = maskObject(req.data);
    if (req.query_string) {
      // query string may be a raw string - mask via fake URL
      req.query_string = maskUrl(`https://x?${req.query_string}`).split('?')[1] ?? '';
    }
    sanitized.request = req;
  }

  // --- user ---
  if (sanitized.user) {
    const u: SanitizableEvent['user'] = {};
    if (opts.keepUserId && sanitized.user.id) u.id = sanitized.user.id;
    if (sanitized.user.email && opts.hashEmail) {
      // basic fnv-like hash for correlation without PII
      u.email = `hash:${simpleHash(sanitized.user.email)}`;
    }
    if (!opts.stripIp && sanitized.user.ip_address) {
      u.ip_address = sanitized.user.ip_address;
    }
    // drop username always
    sanitized.user = u;
  }

  // --- extra / contexts ---
  if (sanitized.extra) sanitized.extra = maskObject(sanitized.extra);
  if (sanitized.contexts) sanitized.contexts = maskObject(sanitized.contexts);

  // --- breadcrumbs ---
  if (sanitized.breadcrumbs) {
    sanitized.breadcrumbs = sanitized.breadcrumbs.map((b) => ({
      ...b,
      data: b.data !== undefined ? maskObject(b.data) : undefined,
    }));
  }

  return sanitized;
};

/**
 * Thin adapter for SDK event types that are structurally compatible at runtime
 * but not assignable under exactOptionalPropertyTypes.
 */
export const sanitizeSentryEventPayload = <T>(event: T, options: SanitizeOptions = {}): T => {
  if (!event || typeof event !== 'object') {
    return event;
  }

  return sanitizeSentryEvent(event as T & SanitizableEvent, options);
};

/** Non-crypto hash for email correlation in Sentry. Not a security primitive. */
const simpleHash = (input: string): string => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
};
